'use client';
import React from 'react';
import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { useSearchParams } from 'next/navigation';
import { CtaButton } from '@web/molecules';
import { BoxedCountdownClock } from '../BoxedCountdownClock';
import { useReporter } from '../../reporter';
import { MuxVideo } from '../MuxVideo';
import { EmailCapture } from '../EmailCaptureBar/EmailCapture';
import { CTAGroup } from '../CTAGroup';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import { orientationSettings } from './constants';
import { heroButtonClicked } from './events';
import {
  StyledHero,
  ContentContainer,
  Content,
  Header,
  Subheader,
  VideoHeroCtaShell,
  InputWrapper,
  TextWrapper,
  EyebrowText,
  CountdownAndLink,
  VideoWrapper,
} from './styles.js';

export const VideoHero = ({
  eyebrowText,
  headline,
  headlineVariants,
  subheadline,
  cta,
  backgroundColor = 'secondary.plum',
  fontColor = 'text.lightest',
  horizontalOrientation = 'left',
  targetDate = null,
  video,
  level,
  typography = 'heading3',
}) => {
  const reporter = useReporter();
  const searchParams = useSearchParams();
  const headlineVariant = searchParams?.get('hpkeyword');
  const { formattedAmount } = useShopifyOfferContext();

  if (headlineVariant && headlineVariants) {
    const variant = headlineVariants.find(e => e.variant === headlineVariant);
    if (variant) {
      headline = variant.heading;
    }
  }

  return (
    <StyledHero backgroundColor={backgroundColor}>
      <ContentContainer
        horizontalOrientation={orientationSettings[horizontalOrientation]}
      >
        <Content
          horizontalOrientation={orientationSettings[horizontalOrientation]}
        >
          <TextWrapper>
            {eyebrowText && (
              <EyebrowText $fontColor={fontColor}>
                {interpolate(eyebrowText, { offer: formattedAmount })}
              </EyebrowText>
            )}
            {headline && (
              <Header
                $fontColor={fontColor}
                $typography={typography}
                as={level}
              >
                {interpolate(headline, { offer: formattedAmount })}
              </Header>
            )}
            {subheadline && (
              <Subheader $fontColor={fontColor}>
                {interpolate(subheadline, { offer: formattedAmount })}
              </Subheader>
            )}
            {(cta || targetDate) && (
              <CountdownAndLink>
                {cta && cta.component === 'email_capture' ? (
                  <InputWrapper>
                    <EmailCapture {...cta} buttonVariant={cta.variant} />
                  </InputWrapper>
                ) : cta && cta.component === 'cta_group' ? (
                  <CTAGroup {...cta} />
                ) : (
                  cta && (
                    <VideoHeroCtaShell
                      fullWidth={cta.fullWidth}
                      fullWidthMobile={cta.fullWidthMobile}
                      fullWidthDesktop={cta.fullWidthDesktop}
                    >
                      <CtaButton
                        {...cta}
                        href={cta.target}
                        variant={cta.variant}
                        icon={cta.icon}
                        iconAlignment={cta.iconAlignment}
                        headerText={cta.headerText}
                        onClick={() =>
                          reporter.tag(heroButtonClicked(cta.text, cta.target))
                        }
                      >
                        {cta.text}
                      </CtaButton>
                    </VideoHeroCtaShell>
                  )
                )}
                {targetDate && (
                  <BoxedCountdownClock
                    targetDate={targetDate}
                    textColor={fontColor}
                  />
                )}
              </CountdownAndLink>
            )}
          </TextWrapper>
        </Content>
        <VideoWrapper $aspectRatio={video.aspectRatio || '4 / 3'}>
          <MuxVideo
            playbackId={video.playbackId}
            autoplay={video.autoplay}
            loop={video.loop}
            thumbnail={video.thumbnail}
            aspectRatio={video.aspectRatio}
          />
        </VideoWrapper>
      </ContentContainer>
    </StyledHero>
  );
};

VideoHero.displayName = 'Video Hero';

VideoHero.propTypes = {
  eyebrowText: PropTypes.string,
  headline: PropTypes.node,
  headlineVariants: PropTypes.array,
  subheadline: PropTypes.string,
  cta: PropTypes.object,
  backgroundColor: PropTypes.string,
  fontColor: PropTypes.string,
  image: PropTypes.object.isRequired,
  horizontalOrientation: PropTypes.oneOf(['left', 'center', 'right']),
  targetDate: PropTypes.instanceOf(Date), //eslint-disable-line react/forbid-prop-types,
  video: PropTypes.object.isRequired,
  typography: PropTypes.string,
};
